/**
 * DC «Коммерция → Лог» — тотальный лог действий клиентского приложения.
 * Источник: GET /api/commerce/activity (perm commerce). По умолчанию события
 * сгруппированы в СЕССИИ (визит игрока: вход → действия → выход), карточка
 * раскрывается в список действий. Есть и плоский вид. PII расшифровывает бэкенд.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from "react";
import { useAvatarSrc } from "../../hooks/useAvatarSrc";
import { api } from "../../api/client";
import { describeVestnik, isVestnikEvent } from "./dcVestnikLog";
import css from "./dcSections.module.css";

interface ActivityItem {
  id: number;
  ts: string;
  telegramId: number | null;
  category: string;
  kind: string;
  screen: string;
  label: string;
  refId: string;
  questId: string;
  actorStaffId: string;
  actorName: string;
  amount: number | null;
  user: { telegramId: number | null; name: string; username: string; phone: string; avatarUrl?: string; addedBy?: string; firstGame?: string };
  meta: unknown;
}
interface ActivityResponse { items: ActivityItem[]; total: number; limit: number; offset: number; }

const KIND_LABELS: Record<string, string> = {
  view: "Переход", route: "Переход", view_quest: "Просмотр квеста", view_special: "Просмотр спецверсии", view_actor: "Просмотр досье",
  tap: "Нажатие", app_open: "Вход в бот",
  book: "Бронь", special_book: "Спецбронь",
  fan_join: "Фан-клуб: вступление", fan_leave: "Фан-клуб: выход",
  shop_buy: "Лавка: покупка", uc: "Юникоины", actor_tip: "Награда актёрам", quiz_answer: "Ответ викторины",
  link: "Поделился телефоном", phone_declined: "Не поделился телефоном", session_join: "Подключение к сессии", invite: "Приглашение", offer_lead: "Заявка по офферу",
};
const CATEGORY_LABELS: Record<string, string> = { nav: "Навигация", commerce: "Коммерция", session: "Сессии", account: "Аккаунт" };
// «Газета» — не category, а сквозной срез (см. dcVestnikLog): события материалов
// живут и в nav, и в commerce. На сервер уходит scope=vestnik, а не category.
const VESTNIK_CAT = "vestnik";
const CATEGORY_SHORT: Record<string, string> = { nav: "Нав", commerce: "Ком", session: "Сес", account: "Акк", vestnik: "Газ" };
const CATEGORY_COLOR: Record<string, string> = { nav: "#7a8aa0", commerce: "#3ad29f", session: "#5aa9ff", account: "#caa24a", vestnik: "#d98ae0" };
const LIMIT = 400;
const SESSION_GAP_MS = 30 * 60 * 1000; // новая сессия, если разрыв > 30 мин (или событие app_open)

function kindLabel(k: string): string { return KIND_LABELS[k] ?? k; }
/** Категория для чипа: события газеты помечаем «Газ», даже если лежат в nav/commerce. */
function chipCategory(e: ActivityItem): string { return isVestnikEvent(e) ? VESTNIK_CAT : e.category; }

function tsMs(ts: string): number {
  if (!ts) return 0;
  const iso = ts.includes("T") ? ts : ts.replace(" ", "T") + "Z";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}
/** UTC → бизнес-время UTC+5, «DD.MM HH:MM». */
function fmtTs(ts: string): string {
  const ms = tsMs(ts); if (!ms) return ts || "—";
  const s = new Date(ms + 5 * 3600 * 1000); const p = (n: number) => String(n).padStart(2, "0");
  return `${p(s.getUTCDate())}.${p(s.getUTCMonth() + 1)} ${p(s.getUTCHours())}:${p(s.getUTCMinutes())}`;
}
/** «HH:MM» UTC+5. */
function fmtTime(ts: string): string {
  const ms = tsMs(ts); if (!ms) return "—";
  const s = new Date(ms + 5 * 3600 * 1000); const p = (n: number) => String(n).padStart(2, "0");
  return `${p(s.getUTCHours())}:${p(s.getUTCMinutes())}`;
}
function userLabel(u: ActivityItem["user"], telegramId: number | null): string {
  if (u.name) return u.name;
  if (u.username) return "@" + u.username;
  const tid = u.telegramId ?? telegramId;
  return tid ? "id" + tid : "—";
}
function userDetail(u: ActivityItem["user"], telegramId: number | null): string {
  const tid = u.telegramId ?? telegramId;
  const parts = [u.username ? "@" + u.username : "", tid ? "id" + tid : "", u.phone || ""].filter(Boolean);
  if (!u.name && u.addedBy) parts.push("добавил: " + u.addedBy);
  if (u.firstGame) parts.push("перв. игра: " + u.firstGame);
  return parts.join(" · ") || "нет данных";
}

/** Мини-аватар Telegram с фолбэком на инициал (для строк лога). */
function UserAvatar({ url, name, size = 26 }: { url?: string; name: string; size?: number }): ReactElement {
  const { src, failed } = useAvatarSrc(url); // загрузка через глобальный семафор (см. useAvatarSrc)
  const ch = (name || "?").trim().charAt(0).toUpperCase() || "?";
  const hue = Math.abs([...(name || "?")].reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7)) % 360;
  return (
    <span style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", flex: "0 0 auto", display: "inline-grid", placeItems: "center", background: `hsl(${hue} 30% 26%)`, color: "var(--text-1)", fontSize: Math.round(size * 0.44), fontWeight: 700 }}>
      {src && !failed ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : ch}
    </span>
  );
}
/** Человекочитаемое описание действия — как будто рассказываем про человека, а не дамп событий. */
function describeAction(e: ActivityItem): string {
  const vestnik = describeVestnik(e);
  if (vestnik) return vestnik;
  const name = (e.label || e.screen || "").replace(/^tab:/, "").trim();
  const q = (s?: string) => (s ? ` «${s}»` : "");
  const actor = e.actorName || e.actorStaffId; // имя актёра, id — только как фолбэк
  switch (e.kind) {
    case "app_open": return "Вошёл в бот";
    case "tap": return name ? `Нажал «${name}»` : "Нажал кнопку";
    case "view":
    case "route": return name ? `Перешёл в «${name}»` : "Открыл экран";
    case "view_quest": return "Открыл карточку квеста" + q(e.questId);
    case "view_special": return "Открыл спецверсию" + (e.refId ? ` #${e.refId}` : "");
    case "view_actor": return "Открыл досье актёра" + q(actor);
    case "link": return "Поделился телефоном";
    case "phone_declined": return "Не поделился телефоном (отклонил доступ)";
    case "book": return "Забронировал" + q(e.questId);
    case "special_book": return "Забронировал спецверсию" + q(e.questId);
    case "fan_join": return "Вступил в фан-клуб" + q(actor);
    case "fan_leave": return "Вышел из фан-клуба" + q(actor);
    case "shop_buy": return "Покупка в Лавке" + (e.refId ? ` #${e.refId}` : "");
    case "quiz_answer": return "Ответил в викторине";
    case "session_join": return "Подключился к сессии" + (e.refId ? ` #${e.refId}` : "");
    case "invite": return "Пригласил друга";
    case "offer_lead": return "Оставил заявку по офферу" + (e.label ? `: ${e.label}` : "");
    default: {
      const base = KIND_LABELS[e.kind] ?? e.kind;
      const main = e.label || e.screen;
      return main && main !== base ? `${base}: ${main}` : base;
    }
  }
}

interface Session {
  key: string;
  telegramId: number | null;
  user: ActivityItem["user"];
  start: string;
  end: string;
  events: ActivityItem[];
}
function buildSessions(items: ActivityItem[]): Session[] {
  const byUser = new Map<string, ActivityItem[]>();
  for (const it of items) {
    const k = String(it.telegramId ?? "anon");
    const arr = byUser.get(k);
    if (arr) arr.push(it); else byUser.set(k, [it]);
  }
  const out: Session[] = [];
  byUser.forEach((evs, k) => {
    const sorted = [...evs].sort((a, b) => tsMs(a.ts) - tsMs(b.ts));
    let cur: ActivityItem[] = [];
    const flush = () => {
      if (!cur.length) return;
      const first = cur[0]!; const last = cur[cur.length - 1]!;
      out.push({ key: `${k}-${first.id}`, telegramId: first.telegramId, user: first.user, start: first.ts, end: last.ts, events: cur });
      cur = [];
    };
    for (const e of sorted) {
      const prev = cur[cur.length - 1];
      if (prev && (e.kind === "app_open" || tsMs(e.ts) - tsMs(prev.ts) > SESSION_GAP_MS)) flush();
      cur.push(e);
    }
    flush();
  });
  out.sort((a, b) => tsMs(b.end) - tsMs(a.end));
  return out;
}
function sessionUc(s: Session): number {
  let uc = 0;
  for (const e of s.events) if (e.amount != null) uc += e.amount;
  return Math.round(uc * 100) / 100;
}

const wrap: CSSProperties = { padding: "10px 12px 28px" };
const filterRow: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 8, alignItems: "center" };
const inp: CSSProperties = { boxSizing: "border-box", padding: "6px 9px", background: "var(--surface-1)", border: "1px solid var(--surface-3)", borderRadius: 7, color: "var(--text-1)", fontSize: 12.5, fontFamily: "inherit" };
const sel: CSSProperties = { ...inp, cursor: "pointer" };
const miniBtn: CSSProperties = { padding: "6px 11px", borderRadius: 7, border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-2)", fontSize: 12, cursor: "pointer" };
const chip = (cat: string): CSSProperties => ({ display: "inline-block", padding: "1px 6px", borderRadius: 6, fontSize: 10.5, fontWeight: 700, color: "#06121a", background: CATEGORY_COLOR[cat] ?? "#7a8aa0", whiteSpace: "nowrap" });

const sCard: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 9, background: "var(--surface-1)", marginBottom: 6, overflow: "hidden" };
const sHead: CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer" };
const sName: CSSProperties = { fontWeight: 700, fontSize: 13, color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 };
const sMeta: CSSProperties = { fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap" };
const dropBtn: CSSProperties = { border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-3)", borderRadius: 5, fontSize: 10, lineHeight: 1, padding: "2px 5px", cursor: "pointer", flex: "0 0 auto" };
const actRow: CSSProperties = { display: "flex", alignItems: "baseline", gap: 8, padding: "5px 10px", borderTop: "1px solid var(--surface-2)", fontSize: 12 };

export function DcActivityLogSection({ onToast, initialKind, initialRefId }: { onToast: (m: string) => void; initialKind?: string; initialRefId?: string }) {
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [kind, setKind] = useState(initialKind ?? "");
  const [refId, setRefId] = useState(initialRefId ?? "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [view, setView] = useState<"sessions" | "flat">("sessions");
  const [auto, setAuto] = useState(true);
  const [hideStaff, setHideStaff] = useState(true);

  const [items, setItems] = useState<ActivityItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openSessions, setOpenSessions] = useState<Set<string>>(new Set());
  const [openUsers, setOpenUsers] = useState<Set<string>>(new Set());
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    setKind(initialKind ?? "");
    setRefId(initialRefId ?? "");
  }, [initialKind, initialRefId]);

  const baseQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (qDebounced) p.set("q", qDebounced);
    if (category === VESTNIK_CAT) p.set("scope", VESTNIK_CAT);
    else if (category) p.set("category", category);
    if (kind) p.set("kind", kind);
    if (refId) p.set("refId", refId);
    if (from) p.set("from", `${from} 00:00:00`);
    if (to) p.set("to", `${to} 23:59:59`);
    if (hideStaff) p.set("excludeStaff", "1");
    p.set("sort", "ts");
    p.set("dir", "desc");
    p.set("limit", String(LIMIT));
    return p;
  }, [qDebounced, category, kind, refId, from, to, hideStaff]);

  const fetchAt = useCallback(async (offset: number, signal: AbortSignal): Promise<ActivityResponse> => {
    const p = new URLSearchParams(baseQuery);
    p.set("offset", String(offset));
    return api<ActivityResponse>(`/api/commerce/activity?${p.toString()}`, { signal });
  }, [baseQuery]);

  // Свежая загрузка (сброс) при смене фильтров.
  const reload = useCallback((silent = false) => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    if (!silent) setLoading(true);
    setError(null);
    fetchAt(0, ac.signal)
      .then((r) => { if (ac.signal.aborted) return; setItems(r.items ?? []); setTotal(r.total ?? 0); setLoading(false); })
      .catch((e: unknown) => { if (ac.signal.aborted) return; setError(e instanceof Error ? e.message : "Ошибка"); setLoading(false); });
  }, [fetchAt]);

  useEffect(() => { reload(); return () => abortRef.current?.abort(); }, [reload]);

  // Авто-подмешивание новых событий БЕЗ сброса подгруженного: тянем свежее окно и
  // добавляем сверху только те, которых ещё нет (по id). Вся загруженная история цела.
  const refreshNew = useCallback(() => {
    const ac = new AbortController();
    fetchAt(0, ac.signal)
      .then((r) => {
        const fresh = r.items ?? [];
        setTotal(r.total ?? 0);
        setItems((cur) => {
          if (cur.length === 0) return fresh;
          const have = new Set(cur.map((x) => x.id));
          const add = fresh.filter((x) => !have.has(x.id));
          return add.length ? [...add, ...cur] : cur;
        });
      })
      .catch(() => {});
  }, [fetchAt]);

  // Авто-рефреш: только подмешиваем новое, не трогая подгруженные страницы.
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => refreshNew(), 15000);
    return () => clearInterval(t);
  }, [auto, refreshNew]);

  const loadMore = () => {
    if (loadingMore || items.length >= total) return;
    setLoadingMore(true);
    const ac = new AbortController();
    fetchAt(items.length, ac.signal)
      .then((r) => setItems((cur) => [...cur, ...(r.items ?? [])]))
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const sessions = useMemo(() => buildSessions(items), [items]);
  const toggleSession = (k: string) => setOpenSessions((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleUser = (k: string) => setOpenUsers((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const exportCsv = () => {
    const head = ["Время(UTC+5)", "Пользователь", "@username", "Телефон", "Категория", "Тип", "Действие", "Сумма", "Квест", "Актёр", "Ref"];
    const rows = items.map((i) => [
      fmtTs(i.ts), i.user.name, i.user.username, i.user.phone,
      chipCategory(i) === VESTNIK_CAT ? "Газета" : (CATEGORY_LABELS[i.category] ?? i.category), kindLabel(i.kind), describeAction(i),
      i.amount == null ? "" : String(i.amount), i.questId, i.actorStaffId, i.refId,
    ]);
    const esc = (v: string) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [head, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `activity-log-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    onToast("CSV выгружен (загруженное окно)");
  };

  return (
    <div className={css.main}>
      <div style={wrap}>
        <div style={filterRow}>
          <input style={{ ...inp, flex: "1 1 180px" }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск: имя, @username, telegram id, телефон" />
          <select style={sel} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Все категории</option>
            <option value={VESTNIK_CAT}>Газета</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select style={sel} value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Все типы</option>
            {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input style={{ ...inp, width: 138 }} type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="С даты" />
          <input style={{ ...inp, width: 138 }} type="date" value={to} onChange={(e) => setTo(e.target.value)} title="По дату" />
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-2)", cursor: "pointer" }}>
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> авто
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-2)", cursor: "pointer" }} title="Скрыть события сотрудников (по telegram_id из staff)">
            <input type="checkbox" checked={hideStaff} onChange={(e) => setHideStaff(e.target.checked)} /> без сотрудников
          </label>
          <button type="button" style={miniBtn} onClick={() => reload()}>Обновить</button>
          {refId ? <button type="button" style={miniBtn} onClick={() => { setKind(""); setRefId(""); }}>Сбросить лиды #{refId}</button> : null}
          <button type="button" style={miniBtn} onClick={exportCsv} disabled={!items.length}>CSV</button>
        </div>

        {/* Переключатель вида */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {(["sessions", "flat"] as const).map((v) => (
            <button key={v} type="button" onClick={() => setView(v)}
              style={{ ...miniBtn, padding: "5px 12px", background: view === v ? CATEGORY_COLOR.commerce : "var(--surface-2)", color: view === v ? "#06121a" : "var(--text-2)", fontWeight: view === v ? 700 : 400, border: view === v ? "1px solid transparent" : miniBtn.border }}>
              {v === "sessions" ? "Сессии" : "Все события"}
            </button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)", alignSelf: "center" }}>
            {view === "sessions" ? `${sessions.length} сессий` : ""} загружено {items.length} из {total}
          </span>
        </div>

        {error ? (
          <div className={css.err}>Не удалось загрузить лог: {error}</div>
        ) : loading ? (
          <div style={{ padding: "16px 8px", fontSize: 12, color: "var(--text-3)" }}>Загрузка…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: "24px 8px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>Событий по фильтру нет.</div>
        ) : view === "sessions" ? (
          <div>
            {sessions.map((s) => {
              const open = openSessions.has(s.key);
              const uc = sessionUc(s);
              const same = s.start.slice(0, 10) === s.end.slice(0, 10);
              return (
                <div key={s.key} style={sCard}>
                  <div style={sHead} onClick={() => toggleSession(s.key)}>
                    <span style={{ fontSize: 11, color: "var(--text-3)" }}>{open ? "▾" : "▸"}</span>
                    <UserAvatar url={s.user.avatarUrl} name={userLabel(s.user, s.telegramId)} size={26} />
                    <span style={sName} title={userDetail(s.user, s.telegramId)}>{userLabel(s.user, s.telegramId)}</span>
                    <button type="button" style={dropBtn} title="Детали пользователя"
                      onClick={(e) => { e.stopPropagation(); toggleUser(s.key); }}>id</button>
                    <span style={{ ...sMeta, marginLeft: "auto" }}>
                      {fmtTs(s.start)}{same ? `–${fmtTime(s.end)}` : ` … ${fmtTs(s.end)}`} · {s.events.length} действ.
                    </span>
                    {uc !== 0 ? <span style={{ fontSize: 11, fontWeight: 700, color: uc < 0 ? "#ff6b6b" : "#3ad29f", whiteSpace: "nowrap" }}>{uc > 0 ? "+" : ""}{uc} UC</span> : null}
                  </div>
                  {openUsers.has(s.key) ? (
                    <div style={{ padding: "0 10px 7px 26px", fontSize: 11, color: "var(--text-2)" }}>{userDetail(s.user, s.telegramId)}</div>
                  ) : null}
                  {open ? (
                    <div>
                      {s.events.map((e) => (
                        <div key={e.id} style={actRow}>
                          <span style={{ color: "var(--text-3)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmtTime(e.ts)}</span>
                          <span style={chip(chipCategory(e))} title={chipCategory(e) === VESTNIK_CAT ? "Газета" : (CATEGORY_LABELS[e.category] ?? e.category)}>{CATEGORY_SHORT[chipCategory(e)] ?? e.category}</span>
                          <span style={{ color: "var(--text-1)", flex: 1, minWidth: 0 }}>{describeAction(e)}</span>
                          {e.amount != null ? <span style={{ fontWeight: 700, color: e.amount < 0 ? "#ff6b6b" : "#3ad29f", whiteSpace: "nowrap" }}>{e.amount > 0 ? "+" : ""}{Math.round(e.amount * 100) / 100}</span> : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            {items.map((i) => {
              const uOpen = openUsers.has("flat-" + i.id);
              return (
                <div key={i.id} style={{ ...actRow, borderTop: "1px solid var(--surface-2)", flexWrap: "wrap" }}>
                  <span style={{ color: "var(--text-3)", whiteSpace: "nowrap" }}>{fmtTs(i.ts)}</span>
                  <span style={chip(chipCategory(i))} title={chipCategory(i) === VESTNIK_CAT ? "Газета" : (CATEGORY_LABELS[i.category] ?? i.category)}>{CATEGORY_SHORT[chipCategory(i)] ?? i.category}</span>
                  <UserAvatar url={i.user.avatarUrl} name={userLabel(i.user, i.telegramId)} size={22} />
                  <span style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }} title={userDetail(i.user, i.telegramId)}>{userLabel(i.user, i.telegramId)}</span>
                  <button type="button" style={dropBtn} onClick={() => toggleUser("flat-" + i.id)}>id</button>
                  <span style={{ color: "var(--text-1)", flex: 1, minWidth: 80 }}>{describeAction(i)}</span>
                  {i.amount != null ? <span style={{ fontWeight: 700, color: i.amount < 0 ? "#ff6b6b" : "#3ad29f", whiteSpace: "nowrap" }}>{i.amount > 0 ? "+" : ""}{Math.round(i.amount * 100) / 100}</span> : null}
                  {uOpen ? <span style={{ flex: "1 1 100%", fontSize: 11, color: "var(--text-3)", paddingLeft: 4 }}>{userDetail(i.user, i.telegramId)}</span> : null}
                </div>
              );
            })}
          </div>
        )}

        {!error && items.length > 0 && items.length < total ? (
          <button type="button" style={{ ...miniBtn, width: "100%", marginTop: 10, padding: "9px" }} onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Загрузка…" : `Показать ещё (загружено ${items.length} из ${total})`}
          </button>
        ) : null}
      </div>
    </div>
  );
}
